/**
 * The shelf itself — one row per CATALOG UNIT.
 *
 * The unit is the offerable grain: the only thing a learner can be
 * dealt, and therefore the only thing an author authors and a human
 * reviews.  What each row shows is what a reviewer decides on — what it
 * is called, what shape it is offered in, whether it is safe to walk in
 * cold, what claim it stands on, how much is inside it and of what — and
 * everything else waits behind the row.
 *
 * Rows rather than cards: a reviewer is comparing, and comparison wants
 * the same fact in the same place on every line.
 */

import type { Unit } from "./types";
import { Badge, Check, Id, LevelSpread, MONO, Nothing, ReviewBadge } from "./ui";

function Row({
  unit,
  open,
  picked,
  showPipeline,
  onOpen,
  onPick,
}: {
  unit: Unit;
  open: boolean;
  picked: boolean;
  /** More than one pipeline has shelved something, so saying which one
   *  made this vessel distinguishes it from its neighbours. */
  showPipeline: boolean;
  onOpen: () => void;
  onPick: (next: boolean) => void;
}) {
  // The manifest's declared planet disagreeing with the one resolved
  // from the primary claim is a real defect, and the library resolves
  // rather than trusts precisely so it can be seen.
  const planetSkew = Boolean(unit.vesselPlanet) && unit.vesselPlanet !== unit.planet;
  const instruments = unit.instruments.length;

  return (
    // A DIV, not a button.  The row carries a checkbox, and a checkbox
    // inside a button is invalid HTML that browsers resolve by
    // swallowing the inner control's clicks — the selection would look
    // present and never tick.
    <div
      role="button"
      tabIndex={0}
      // Keyed by the PERMANENT name, not the address: a republish moves
      // every address on the shelf, and a test (or a reader) holding a
      // row would lose it for no reason a human would recognize.
      data-testid={`unit-row-${unit.permanentRef}`}
      data-vessel={unit.vesselType}
      data-review={unit.review}
      data-entry={unit.entry ? "true" : "false"}
      data-whole={unit.offeredWhole ? "true" : "false"}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      style={{
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr) auto",
        gap: "4px 12px",
        width: "100%",
        padding: "10px 14px",
        borderBottom: "1px solid var(--color-border-subtle)",
        background: open ? "var(--color-primary-muted)" : "transparent",
        textAlign: "left",
        cursor: "pointer",
        fontFamily: "inherit",
        color: "var(--color-text)",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", paddingTop: 2 }}>
        <Check
          testId={`unit-pick-${unit.permanentRef}`}
          checked={picked}
          onChange={onPick}
          label={`select ${unit.permanentRef}`}
        />
      </div>

      <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {/* The TITLE leads, because a unit has one and it is prose: this
            is the line a reviewer recognizes the content by.  The ref is
            underneath, where it is still there to match against. */}
        <span
          style={{
            fontSize: 13.5,
            fontWeight: 550,
            color: open ? "var(--color-primary)" : "var(--color-text)",
          }}
        >
          {unit.title || unit.permanentRef}
        </span>
        <ReviewBadge state={unit.review} />
        {unit.offeredWhole ? (
          <Badge
            tone="accent"
            title="the author declared no units — this vessel is offerable only whole, which is itself one unit"
          >
            whole
          </Badge>
        ) : null}
        {/* The author's veto, badged only when it is ON.  A marker on
            every safely-enterable row would be noise across the whole
            shelf, and the exception is what a reviewer is hunting. */}
        {unit.entry ? null : (
          <Badge
            tone="warn"
            title="entry:false — the author's veto on cold entry; reachable inside a whole-vessel serve or after its predecessor, never offered standalone"
          >
            no cold entry
          </Badge>
        )}
        {unit.versions > 1 ? (
          <Badge mono title={`${unit.versions} published versions of this vessel`}>
            v{unit.version}
          </Badge>
        ) : null}
        {/* Only when more than one pipeline has shelved anything: until
            then the badge would be on every row and say nothing. */}
        {unit.pipeline && showPipeline ? (
          <Badge mono title={`forged by ${unit.pipeline}`}>
            {unit.pipeline.replace(/^pipeline_/, "")}
          </Badge>
        ) : null}
        {unit.tombstoned ? <Badge tone="bad">drained</Badge> : null}
        {unit.danglingClaims.length ? (
          <Badge tone="bad" title={`no roster holds ${unit.danglingClaims.join(", ")}`}>
            dangling claim
          </Badge>
        ) : null}
        {planetSkew ? (
          <Badge
            tone="bad"
            title={`the manifest declares ${unit.vesselPlanet}; its primary claim resolves to ${unit.planet || "no planet at all"}`}
          >
            planet skew
          </Badge>
        ) : null}
        {unit.grants ? (
          <Badge tone="accent" title="declares placement — this unit's instruments can grant standing">
            grants {unit.grants}
          </Badge>
        ) : null}
        {/* Notes written against a unit row since purged or superseded.
            Never the verdict — but "somebody flagged this before the
            rewrite" is exactly what the next reviewer needs to know
            before they read it as fresh. */}
        {unit.hasHistory ? (
          <Badge
            tone="neutral"
            title="earlier notes exist on content since purged or superseded — history, not the current verdict"
          >
            history
          </Badge>
        ) : null}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, justifySelf: "end" }}>
        {/* The vessel type in its own vocabulary word.  Prettifying
            `lesson_bench` into "Lesson bench" would give the concept a
            second name, and this page exists to be checked against the
            manifest. */}
        <Badge mono title="vesselType — the shape this unit is offered out of">
          {unit.vesselType || "?"}
        </Badge>
        <LevelSpread levels={unit.levels} />
      </div>

      <div
        style={{
          gridColumn: "2 / -1",
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          fontSize: 11.5,
          color: "var(--color-text-muted)",
        }}
      >
        <Id style={{ fontSize: 11.5 }}>{unit.unitRef}</Id>
        <span>·</span>
        <span style={{ fontFamily: MONO }}>{unit.primaryClaim || "no claim"}</span>
        <span>·</span>
        {/* How much is inside, and of what: the two halves of "is this
            a unit I can review in one sitting". */}
        <span title={instruments ? unit.instruments.join(", ") : "this unit embeds no priceable instrument"}>
          {instruments} instrument{instruments === 1 ? "" : "s"}
          {unit.types.length ? ` — ${unit.types.join(", ")}` : ""}
        </span>
        {unit.minutes ? (
          <>
            <span>·</span>
            <span>{unit.minutes} min</span>
          </>
        ) : null}
        {unit.sourceFile ? (
          <>
            <span>·</span>
            <span style={{ fontFamily: MONO }}>{unit.sourceFile}</span>
          </>
        ) : null}
        {unit.alsoMarks.length ? (
          <>
            <span>·</span>
            <span title={unit.alsoMarks.join(", ")}>also marks {unit.alsoMarks.length}</span>
          </>
        ) : null}
      </div>
    </div>
  );
}

export function UnitList({
  items,
  total,
  shown,
  openRef,
  picked,
  pipelines,
  onOpen,
  onPick,
  onMore,
  loading,
}: {
  /** Every pipeline that exists — the badge is drawn only when a reader
   *  could be looking at vessels from more than one. */
  pipelines?: string[];
  items: Unit[];
  total: number;
  shown: number;
  /** The unit whose detail panel is open, by permanent name — at most one. */
  openRef: string | null;
  /** The units ticked for an action, by permanent name — any number. */
  picked: ReadonlySet<string>;
  onOpen: (permanentRef: string) => void;
  onPick: (permanentRef: string, next: boolean) => void;
  onMore: () => void;
  loading: boolean;
}) {
  if (!loading && items.length === 0) {
    return (
      <Nothing>
        Nothing on this shelf matches the filters.
        <br />
        <span style={{ fontSize: 12 }}>Clear a chip, or pick a wider place in the tree.</span>
      </Nothing>
    );
  }

  return (
    <div data-testid="library-units" style={{ opacity: loading ? 0.55 : 1, transition: "opacity 120ms ease" }}>
      {items.map((unit) => (
        <Row
          key={unit.permanentRef}
          unit={unit}
          open={openRef === unit.permanentRef}
          picked={picked.has(unit.permanentRef)}
          showPipeline={(pipelines?.length ?? 0) > 1}
          onOpen={() => onOpen(unit.permanentRef)}
          onPick={(next) => onPick(unit.permanentRef, next)}
        />
      ))}
      {shown < total ? (
        <div style={{ padding: 14, textAlign: "center" }}>
          <button
            type="button"
            data-testid="library-more"
            onClick={onMore}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid var(--color-border)",
              background: "transparent",
              color: "var(--color-text-secondary)",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
            }}
          >
            show more — {shown} of {total}
          </button>
        </div>
      ) : null}
    </div>
  );
}
